/**
 * @summary Plugin for Content Subpage implementation as a custom component
 * @description The Content Subpage will be nested as a direct child under the main-content
 * to allow interaction of the App Header to be possible.
 * This would mean that the footer would also be interactable if it exists.
 */

import { createApp } from "vue";
import DOMUtils from "@utils/dom.utils";
import ContentSubpageContainer from "./components/ContentSubpageContainer";
import ContentSubpageComponent from "./components/ContentSubpage";
import AppInstanceUtils from "@plugins/common/service/app.instance.utils";

const ContentSubpage = {
  install(app) {
    app.component("content-subpage", ContentSubpageComponent);
    app.component("content-subpage-container", ContentSubpageContainer);
    const showSubpage = (subpage, params, listeners) => {
      const root = params && params.root ? params.root : ContentSubpage;
      const container = getContentSubpageContainer(root);
      return container.add(subpage, params, listeners);
    };

    const removeAllContentSubpages = (params) => {
      const root = params && params.root ? params.root : ContentSubpage;
      const container = getContentSubpageContainer(root);
      container.removeAll();
    };

    const hasOpenedContentSubpage = (params) => {
      const root = params && params.root ? params.root : ContentSubpage;
      const container = getContentSubpageContainer(root);
      return container.hasOpenedContentSubpage();
    };

    const getCurrentBreadcrumb = (params) => {
      const root = params && params.root ? params.root : ContentSubpage;
      const container = getContentSubpageContainer(root);
      return container.getCurrentBreadcrumb();
    };

    const forceHideSubPages = (params) => {
      const root = params && params.root ? params.root : ContentSubpage;
      const container = getContentSubpageContainer(root);
      return container.forceHideSubPages();
    };

    const showHiddenSubPages = (params) => {
      const root = params && params.root ? params.root : ContentSubpage;
      const container = getContentSubpageContainer(root);
      return container.showHiddenSubpages();
    };

    /**
     * Plugin API
     */
    app.config.globalProperties.$contentSubpage = {
      show(subpage, params = {}, listeners = {}) {
        if (typeof subpage === "object" || typeof subpage === "function") {
          return showSubpage(subpage, params, listeners);
        }
      },

      removeAll(params = {}) {
        removeAllContentSubpages(params);
      },

      hasOpenedContentSubpage(params = {}) {
        return hasOpenedContentSubpage(params);
      },

      getCurrentBreadcrumb(params = {}) {
        return getCurrentBreadcrumb(params);
      },

      forceHideSubPages(params = {}) {
        return forceHideSubPages(params);
      },

      showHiddenSubPages(params = {}) {
        return showHiddenSubPages(params);
      },
    };
    this.$contentSubpage = app.config.globalProperties.$contentSubpage;
  },
};

let ContentSubpageContainerComponent;

/**
 * @summary Returns the Vue instance of the subpage container
 * @description An instance of the subpage container is created first if it doesn't exist.
 * The element of the instance is then appended to the App.
 * @param {Object} root - The Vue instance of the root
 *
 */
const getContentSubpageContainer = (plugin) => {
  const contentSubpageContainer = DOMUtils.el(".content-subpage-container");
  if (contentSubpageContainer) {
    plugin.containerComponent = ContentSubpageContainerComponent;
  } else {
    let container = DOMUtils.createDivInBody();

    const app = createApp(ContentSubpageContainer, {
      //parent: plugin
    });

    AppInstanceUtils.installCommonPlugin(app);
    plugin.containerComponent = app.mount(container);
    DOMUtils.replaceComponentWrapperWithComponentElement(plugin.containerComponent);
    ContentSubpageContainerComponent = plugin.containerComponent;
  }

  return plugin.containerComponent;
};
export default ContentSubpage;
